import React, {SyntheticEvent, useEffect, useState} from "react";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import { ICustomerCardType } from "../../../../models/customer-card";
import { ModifyCustomerCardConfirmModal } from "./ModifyCustomerCardConfirmModal";
import { InitContextProps, useAuthContext } from "../../../../services/context/authentication.context";
import { CustomerApplicationType, CardCategory, PickupType, CustomerApplication, withCreatedApplication } from '../../../../models/customer-application';
import PetrolStationService from "services/petrol-station.service";
import Select, { ValueType } from "react-select";
import { ISimplifiedPetrolStation } from "models/petrol-station";
import { DpdParcelShopResponseModel } from "models/dpd-parcel-shop";
import { toast } from "react-toastify";
import { SwitchButton, SwitchButtonState } from "../../../SwitchButton/SwitchButton";
import CustomerApplicationService from "../../../../services/customer-application.service";
import { PaymentCardSection } from "../../PaymentCardSection";
import { DiscountCardSection } from "../../DiscountCardSection";
import { CustomerLocation, isCustomerLocationRequired } from "../../../../models/customer-location";
import { CustomerLocationService } from "../../../../services/customer-location.service";
import DpdParcelShopService from "../../../../services/dpd-parcel-shop.service";
import { PickupOption } from "models/pickup-option";
import { CARD_APPLICATION_MONTHLY_LIMIT_REGEX, cardCategoryLabels, CARD_CATEGORY_TO_ICON } from 'common/constants/cardApplication';
import { AxiosError } from "axios";
import { isEmailValidAndPresent, isPhoneNumber } from "helpers/validators";
import { handleTextInputNumberChange } from "helpers";
import { getPaymentCards } from "../CreditApplicationModal";
import { isNullOrUndefined } from "common/data-util";

interface ModifyCustomerCardProps {
    selectedCardType: ICustomerCardType;
    handleModalHide: () => void;
    application?: CustomerApplication;
    onSuccess?: () => void;
}

interface ModifyCustomerCardForm {
    cardName: string;
    monthlyLimit?: string;
    cardCategory?: CardCategory;
    isVirtual: boolean;
    customerLocationId?: number;
}

interface ModifyCustomerCardModalFormBase {
    contactEmail?: string;
    contactPhone?: string;
    pickupLocation: string;
    pickupLocationId: string | number;
    pickupType: PickupType;
    sameDeliveryContact: boolean;
    recipientEmail: string;
    recipientPhone: string;
}
interface ModifyCustomerCardModalFormPayment extends ModifyCustomerCardModalFormBase {
    type: 'payment';
    cards: Record<CardCategory, ModifyCustomerCardForm[]>;
}
interface ModifyCustomerCardModalFormDiscount extends ModifyCustomerCardModalFormBase {
    type: 'discount';
    cards: ModifyCustomerCardForm[];
}
type ModifyCustomerCardModalForm = ModifyCustomerCardModalFormPayment | ModifyCustomerCardModalFormDiscount;

const getInitialCardsStateDiscount = (): ModifyCustomerCardForm[] => [{ cardName: '', monthlyLimit: undefined, cardCategory: undefined, isVirtual: false }];


export function ModifyCustomerCardModal(props: Readonly<ModifyCustomerCardProps>) {
    const { state, dispatch }: InitContextProps = useAuthContext();
    const { customerId, customer } = state;

    const [stations, setStations] = useState<ISimplifiedPetrolStation[]>([])
    const [isLoadingStations, setIsLoadingStations] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savingError, setSavingError] = useState<string>();
    const [validated, setValidated] = useState(false);
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
    
    const base = {
        pickupLocation: props.application?.pickupLocation ?? '',
        pickupType: (props.application?.pickupType as PickupType) ?? 'STATION',
        pickupLocationId: props.application ? (props.application.pickupType === 'STATION' ? Number(props.application.pickupLocationId) : props.application.pickupLocationId) : '',
        contactEmail: props.application?.contact?.email ?? customer.email ?? '',
        contactPhone: props.application?.contact?.phone ?? customer?.phone ?? customer?.mobileNumber ?? '',
        sameDeliveryContact: !props.application?.recipientEmail && !props.application?.recipientPhone,
        recipientEmail: props.application?.recipientEmail ?? '',
        recipientPhone: props.application?.recipientPhone ?? '',
    };

    const getDiscountCards = (): ModifyCustomerCardForm[] => {
        if (props.application?.customerApplicationCards?.length) {
            return props.application.customerApplicationCards.flatMap(group =>
                group.cards.map(card => ({
                    cardName: card.cardName ?? '',
                    monthlyLimit: card.cardLimit != null ? String(card.cardLimit) : undefined,
                    cardCategory: card.cardCategory as CardCategory,
                    isVirtual: false,
                    customerLocationId: card.customerLocationId
                }))
            );
        }
        return getInitialCardsStateDiscount();
    };

    const [form, setForm] = useState<ModifyCustomerCardModalForm>(
        props.selectedCardType === ICustomerCardType.PAYMENT_CARD
            ? { ...base, type: 'payment', cards: getPaymentCards(props.application) }
            : { ...base, type: 'discount', cards: getDiscountCards() }
    );


    const [customerLocations, setCustomerLocations] = useState<CustomerLocation[]>([]);
    const [dpdParcelShops, setDpdParcelShops] = useState<DpdParcelShopResponseModel[]>([]);
    const [isLoadingDpdParcelShops, setIsLoadingDpdParcelShops] = useState(false);

    const isLocationRequired = isCustomerLocationRequired(customerLocations);

    const getStaffedPetrolStations = async () => {
        try {
            setIsLoadingStations(true);

            const res = await PetrolStationService.getStationsWithCardDistribution();
            setStations(res)
        } catch(e) {
            console.error(e);
        } finally {
            setIsLoadingStations(false);
        }
    }

    useEffect(() => {
        getStaffedPetrolStations()
    }, [])

    useEffect(() => {
        if (!customerId) return;

        CustomerLocationService.getCustomerLocations(customerId)
            .then(setCustomerLocations)
            .catch(() => setCustomerLocations([]));
    }, [customerId])

    useEffect(() => {
        setIsLoadingDpdParcelShops(true);
        DpdParcelShopService.getDpdParcelShops()
            .then((shops) => {
                setDpdParcelShops(shops);
            })
            .catch((e) => {
                setDpdParcelShops([]);
                console.error(e);
            })
            .finally(() => {
                setIsLoadingDpdParcelShops(false);
            });
    }, []);

    const hasAnyCards = () => {
        if (form.type === 'payment') {
            return Object.values(CardCategory)
                .filter(cat => cat !== CardCategory.DISCOUNT)
                .some(category => (form.cards[category] as ModifyCustomerCardForm[]).length > 0);
        }
        return form.cards.length > 0;
    };

    const isPickupRequired = () => {
        if (form.type === 'payment') {
            if (!hasAnyCards()) return true;
            return Object.values(CardCategory)
                .filter(cat => cat !== CardCategory.DISCOUNT)
                .some(category => (form.cards[category] as ModifyCustomerCardForm[]).some(card => !card.isVirtual));
        }
        return true;
    };

    const confirmSubmit = () => {
        setValidated(false);
        const isValidForm = validateForm();
        setValidated(true);
        if (isValidForm) {
            setIsConfirmModalVisible(true);
        } else {
            setSaving(false);
        }
    };

    const isCardValid = (card: ModifyCustomerCardForm) => {
        if (!card.cardName.trim()) return false;
        if (props.selectedCardType === ICustomerCardType.PAYMENT_CARD && isNullOrUndefined(card.isVirtual)) return false;
        if (isLocationRequired && isNullOrUndefined(card.customerLocationId)) return false;
        return !(card.monthlyLimit !== undefined && card.monthlyLimit.trim().length > 0 && !CARD_APPLICATION_MONTHLY_LIMIT_REGEX.test(card.monthlyLimit.trim()));
    };

    const validateForm = () => {
        if (isPickupRequired() && !form.pickupLocation) return false;
        if (isPickupRequired() && !form.sameDeliveryContact
            && (!isEmailValidAndPresent(form.recipientEmail) || !isPhoneNumber(form.recipientPhone))) return false;
        if (!isEmailValidAndPresent(form.contactEmail) || !isPhoneNumber(form.contactPhone ?? '')) return false;
        if (!hasAnyCards()) return false;
        if (form.type === 'payment') {
            return Object.values(CardCategory)
                .filter(cat => cat !== CardCategory.DISCOUNT)
                .every(category => (form.cards[category] as ModifyCustomerCardForm[]).every(isCardValid));
        } else {
            return (form.cards as ModifyCustomerCardForm[]).every(isCardValid);
        }
    };

    const handleSubmit = async () => {
        setSaving(true);
        setSavingError(undefined);

        if (!customerId || !customer || (customer && !customer.contactId)) return;

        let allCards: ModifyCustomerCardForm[] = [];
        if (form.type === 'payment') {
            for (const category of Object.values(CardCategory).filter(cat => cat !== CardCategory.DISCOUNT)) {
                const cardsArr = form.cards[category];
                allCards = allCards.concat(cardsArr.map(card => ({ ...card, cardCategory: category })));
            }
        } else {
            allCards = form.cards.map(card => ({ ...card, cardCategory: CardCategory.DISCOUNT }));
        }

        const pickupRequired = isPickupRequired();

        const request = {
            customerId: customerId,
            contactPersonId: customer.contactId,
            customerApplicationType: props.selectedCardType === ICustomerCardType.DISCOUNT_CARD ? CustomerApplicationType.DISCOUNT_CARD_APPLICATION : CustomerApplicationType.PAYMENT_CARD_APPLICATION,
            ...(pickupRequired && {
                pickupType: form.pickupType,
                pickupLocation: form.pickupLocation,
                pickupLocationId: form.pickupLocationId,
                recipientEmail: form.sameDeliveryContact ? undefined : form.recipientEmail.trim(),
                recipientPhone: form.sameDeliveryContact ? undefined : form.recipientPhone.trim(),
            }),
            customerApplicationCards: allCards.map(card => ({
                cardName: card.cardName,
                cardLimit: card.monthlyLimit?.trim() ? Number(card.monthlyLimit) : undefined,
                cardCategory: card.cardCategory,
                cardType: props.selectedCardType,
                isVirtual: card.isVirtual,
                customerLocationId: card.customerLocationId
            })),
            applicantName: state.user.name,
            contactEmail: form.contactEmail,
            contactPhone: form.contactPhone,
        };

        try {
            if (isUpdate) {
                await CustomerApplicationService.update(props.application!.id, request);
            } else {
                const createdApplicationId = await CustomerApplicationService.create(request);

                dispatch({
                    type: "setCustomer",
                    value: {
                        ...state.customer,
                        hasApplications: true,
                        blockingApplications: withCreatedApplication(
                            state.customer.blockingApplications,
                            request.customerApplicationType,
                            createdApplicationId
                        )
                    }
                });
            }

            toast.success("Taotlus edukalt saadetud");
            setTimeout(() => { props.handleModalHide(); props.onSuccess?.(); }, 300);
        } catch (e) {
            const error = e as AxiosError;
            if (error.message && error?.message.includes("Active application already exists")) {
                setSavingError('Aktiivne taotlus on juba olemas.');
            } else {
                setSavingError('Viga salvestamisel, proovi mõne hetke pärast uuesti.');
            }
        } finally {
            setSaving(false);
        }
    };

    const pickupOptions = stations.map(station => ({ label : station.name, value: station.id }))
    const dpdOptions = dpdParcelShops.map(shop => ({ label: shop.companyName, value: shop.parcelShopId }));

    const isUpdate = !!props.application;

    const getTitle = () => {
        if(isUpdate) {
            const applicationNumber = props.application!.id;
            if (props.selectedCardType === ICustomerCardType.PAYMENT_CARD) {
                return `Krediidikaardi taotluse #${applicationNumber} muutmine`;
            } else {
                return `Sooduskaardi taotluse #${applicationNumber} muutmine`;
            }
        }

        if (props.selectedCardType === ICustomerCardType.PAYMENT_CARD) {
            return "Uue krediidikaardi taotlemine";
        }

        return "Uue sooduskaardi taotlemine";
    }

    return (
        <>
            <Modal
                show={true}
                size={"lg"}
                onHide={props.handleModalHide}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        { getTitle() }
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form noValidate validated={validated} onSubmit={(e: SyntheticEvent) => e.preventDefault()}
                          id={"card-form"}
                          className={'d-flex flex-column'}
                    >
                        <div className="row">
                            <h2 className='col-12'>Kontaktisiku andmed</h2>
                            <Form.Group controlId={'form-contact-name'} className={'col-md-6 mb-3'}>
                                <Form.Label>Nimi</Form.Label>
                                <Form.Control
                                    value={props.application?.contact?.name ?? state.user.name}
                                    disabled
                                    type="text"
                                />
                            </Form.Group>
                            <Form.Group controlId={'form-contact-email'} className={'col-md-6 mb-3'}>
                                <Form.Label>Kontaktisiku e-post</Form.Label>
                                <Form.Control
                                    required
                                    value={form.contactEmail}
                                    onChange={e => setForm({ ...form, contactEmail: e.target.value })}
                                    disabled={saving}
                                    isInvalid={validated && !isEmailValidAndPresent(form.contactEmail)}
                                    type="email"
                                    placeholder="E-post"
                                />
                                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group controlId={'form-contact-phone'} className={'col-md-6 mb-3'}>
                                <Form.Label>Kontaktisiku telefon</Form.Label>
                                <InputGroup className="col-12 p-0">
                                    <InputGroup.Prepend>
                                        <InputGroup.Text
                                            id="phone-number-prefix"
                                        >
                                            +372
                                        </InputGroup.Text>
                                    </InputGroup.Prepend>
                                    <Form.Control 
                                        required 
                                        minLength={1}
                                        value={form.contactPhone}
                                        onChange={e => setForm({ ...form, contactPhone: handleTextInputNumberChange(form.contactPhone ?? '', e.target.value) })}
                                        disabled={saving}
                                        type="tel"
                                        placeholder="Telefon"
                                        isInvalid={validated && !isPhoneNumber(form.contactPhone ?? '')}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {!form.contactPhone ? 'Kohustuslik väli' : 'Vigane telefoninumber'}
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </Form.Group>
                        </div>
                        <Form.Group controlId={'form-card-number'} className={'col-sm-12 p-0'}>
                            <h2>Kaardid</h2>
                            {form.type === 'payment' ? (
                                <>
                                    <PaymentCardSection
                                        cards={form.cards as Record<CardCategory, ModifyCustomerCardForm[]>}
                                        setCards={newCards => setForm({ ...form, cards: newCards })}
                                        saving={saving}
                                        categoryLabels={cardCategoryLabels}
                                        categoryIcons={CARD_CATEGORY_TO_ICON}
                                        customerLocations={customerLocations}
                                        validated={validated}
                                    />
                                    {validated && !hasAnyCards() &&
                                        <div className="d-block text-left invalid-feedback u-fs-12">Palun lisage vähemalt üks kaart</div>
                                    }
                                </>
                            ) : (
                                <>
                                    <DiscountCardSection
                                        cards={form.cards as ModifyCustomerCardForm[]}
                                        setCards={newCards => setForm({ ...form, cards: newCards })}
                                        saving={saving}
                                        customerLocations={customerLocations}
                                        validated={validated}
                                    />
                                    {validated && !hasAnyCards() &&
                                        <div className="d-block text-left invalid-feedback u-fs-12">Palun lisage vähemalt üks kaart</div>
                                    }
                                </>
                            )}
                        </Form.Group>
                        { isPickupRequired() && (
                            <Form.Group controlId={'form-card-pickup-location'} className={'col-12 p-0'}>
                                <h2>Kohaletoimetamine</h2>
                                <div className="col-sm-6 p-0">
                                <div className="d-flex">
                                    <SwitchButton
                                    labelLeft="Teenindusjaam"
                                    labelRight="DPD pakiautomaat"
                                    onActiveChange={active => {
                                        setForm({
                                            ...form,
                                            pickupType: active === SwitchButtonState.LEFT ? 'STATION' : 'DPD',
                                            pickupLocation: '',
                                            pickupLocationId: '',
                                        });
                                    }}
                                    active={form.pickupType === 'STATION' ? SwitchButtonState.LEFT : SwitchButtonState.RIGHT}
                                    size="md"
                                />
                                </div>
                                <Form.Label className="mt-2">
                                    <span className="text-nowrap">Kättesaamise koht</span>
                                </Form.Label>
                                {form.pickupType === 'STATION' ? (
                                    <Select
                                        options={pickupOptions}
                                        value={pickupOptions.find(opt => opt.value === form.pickupLocationId) || null}
                                        classNamePrefix={'select'}
                                        onChange={(
                                        val: ValueType<PickupOption>
                                        ) => {
                                        const option = val as PickupOption | null;
                                        setForm({
                                            ...form,
                                            pickupLocation: option ? option.label : '',
                                            pickupLocationId: option ? option.value : '',
                                        });
                                        }}
                                        className={`select-terminal ${!form.pickupLocation && validated ? 'invalid' : ''}`}
                                        placeholder="Vali teenindusjaam"
                                        noOptionsMessage={() => "Valikud puuduvad"}
                                        isLoading={isLoadingStations}
                                        loadingMessage={() => "Laen andmeid..."}
                                    />
                                ) : (
                                    <Select
                                        options={dpdOptions}
                                        value={dpdOptions.find(opt => opt.value === form.pickupLocationId) || null}
                                        classNamePrefix={'select'}
                                        onChange={(
                                        val: ValueType<PickupOption>
                                        ) => {
                                        const option = val as PickupOption | null;
                                        setForm({
                                            ...form,
                                            pickupLocation: option ? option.label : '',
                                            pickupLocationId: option ? option.value : '',
                                        });
                                        }}
                                        className={`select-terminal ${!form.pickupLocation && validated ? 'invalid' : ''}`}
                                        placeholder="Vali DPD pakiautomaat"
                                        noOptionsMessage={() => "Valikud puuduvad"}
                                        isLoading={isLoadingDpdParcelShops}
                                        loadingMessage={() => "Laen andmeid..."}
                                    />
                                )}
                                {
                                    !form.pickupLocation && validated &&
                                <div className="d-block text-right invalid-feedback">Kohustuslik väli</div>
                                }
                                </div>
                                <Form.Check
                                    className="mt-2"
                                    type="checkbox"
                                    label="Kättetoimetamise kontaktid on samad tellija kontaktidega"
                                    checked={form.sameDeliveryContact}
                                    onChange={(e: { target: { checked: boolean; }; }) => setForm({
                                        ...form,
                                        sameDeliveryContact: e.target.checked,
                                        recipientEmail: '',
                                        recipientPhone: '',
                                    })}
                                    disabled={saving}
                                />
                                {!form.sameDeliveryContact && (
                                    <div className="d-flex flex-wrap mt-2">
                                        <Form.Group controlId={'form-recipient-email'} className={'col-md-6 pl-0 mb-1'}>
                                            <Form.Label>Saaja e-post</Form.Label>
                                            <Form.Control
                                                required
                                                value={form.recipientEmail}
                                                onChange={e => setForm({ ...form, recipientEmail: e.target.value })}
                                                disabled={saving}
                                                type="text"
                                                placeholder="Saaja e-post"
                                                isInvalid={validated && !isEmailValidAndPresent(form.recipientEmail)}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {!form.recipientEmail ? 'Kohustuslik väli' : 'Vigane e-post'}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group controlId={'form-recipient-phone'} className={'col-md-6 pr-0 mb-1'}>
                                            <Form.Label>Saaja telefon</Form.Label>
                                            <Form.Control
                                                required
                                                value={form.recipientPhone}
                                                onChange={e => setForm({ ...form, recipientPhone: e.target.value })}
                                                disabled={saving}
                                                type="text"
                                                placeholder="Saaja telefon"
                                                isInvalid={validated && !isPhoneNumber(form.recipientPhone)}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {!form.recipientPhone ? 'Kohustuslik väli' : 'Vigane telefoninumber'}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </div>
                                )}
                            </Form.Group>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer className="d-flex flex-row align-items-end justify-content-around flex-wrap mb-1">
                    <Button 
                        variant="outline-dark"
                        onClick={() => props.handleModalHide()}
                        type="button"
                        disabled={saving}
                        className={"d-flex align-items-center"}
                    >
                        <span className='px-2'>{ isUpdate ? 'Tühista' : 'Loobu' }</span>
                    </Button>
                    <Button variant="primary" onClick={confirmSubmit} type="button" disabled={saving}
                            className="d-flex align-items-center">
                        {
                            !saving && (
                                <span className='px-2'>{ isUpdate ? 'Salvesta' : 'Saada taotlus' }</span>
                            )
                        }
                        {
                            saving &&
                          <>
                            <span className='px-2'>Salvestab…</span>
                            <Spinner size={"sm"} animation={"border"} />
                          </>
                        }
                    </Button>
                </Modal.Footer>
                {
                    !saving && savingError &&
                  <span className="d-flex justify-content-end text-danger px-2 pb-2">{savingError}</span>
                }
            </Modal>

            {
                validated && isConfirmModalVisible && (
                    <ModifyCustomerCardConfirmModal
                        isUpdate={isUpdate}
                        handleConfirmSubmit={() => {
                            setIsConfirmModalVisible(false);
                            handleSubmit()
                        }}
                        handleConfirmReject={() => {
                            setSaving(false);
                            setIsConfirmModalVisible(false)
                        }}
                    />
                )
            }
        </>
    )
}